import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Share2, X, Copy, Check, MessageCircle, Facebook, Twitter, Linkedin, Mail, Loader2 } from 'lucide-react'
import api from '@/services/api'

interface ShareButtonProps {
  propertyId: string
  propertyUrl: string
  propertyTitle: string
  propertyPrice: string
  agentName?: string
  className?: string
  size?: 'sm' | 'md'
}

const ShareButton: React.FC<ShareButtonProps> = ({
  propertyId,
  propertyUrl,
  propertyTitle,
  propertyPrice,
  agentName,
  className = '',
  size = 'md'
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [loading, setLoading] = useState(false)
  const [shortUrl, setShortUrl] = useState<string | null>(null)
  const [shortCode, setShortCode] = useState<string | null>(null)
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 })
  const btnRef = useRef<HTMLButtonElement>(null)

  const getShareUrl = () => shortUrl || propertyUrl

  const shareText = agentName
    ? `${propertyTitle} — ${propertyPrice}\nCompartido por ${agentName}`
    : `${propertyTitle} — ${propertyPrice}`

  const trackEvent = async (platform: string, code: string | null) => {
    if (!code) return
    try {
      await api.post(`/api/agents/p/${code}/track`, { platform })
    } catch {
      // tracking no crítico, no bloquear al usuario
    }
  }

  const generateShortUrl = async (): Promise<{ url: string; code: string } | null> => {
    if (shortUrl && shortCode) return { url: shortUrl, code: shortCode }
    try {
      setLoading(true)
      const response = await api.post('/api/agents/short-urls/generate', { property_id: propertyId })
      if (response.data.success) {
        setShortUrl(response.data.short_url)
        setShortCode(response.data.short_code)
        return { url: response.data.short_url, code: response.data.short_code }
      }
      return null
    } catch {
      return null
    } finally {
      setLoading(false)
    }
  }

  const handleShare = async (platform: string, buildUrl: (url: string) => string) => {
    const result = await generateShortUrl()
    const url = result?.url || propertyUrl
    const code = result?.code || null
    await trackEvent(platform, code)
    window.open(buildUrl(url), '_blank')
    setIsOpen(false)
  }

  const shareOptions = [
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      icon: MessageCircle,
      color: 'hover:bg-green-500/20 text-green-400',
      action: () => handleShare('whatsapp', (url) =>
        `https://wa.me/?text=${encodeURIComponent(url)}`)
    },
    {
      id: 'facebook',
      label: 'Facebook',
      icon: Facebook,
      color: 'hover:bg-blue-600/20 text-blue-400',
      action: () => handleShare('facebook', (url) =>
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`)
    },
    {
      id: 'twitter',
      label: 'Twitter / X',
      icon: Twitter,
      color: 'hover:bg-sky-500/20 text-sky-400',
      action: () => handleShare('twitter', (url) =>
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`)
    },
    {
      id: 'linkedin',
      label: 'LinkedIn',
      icon: Linkedin,
      color: 'hover:bg-blue-500/20 text-blue-300',
      action: () => handleShare('linkedin', (url) =>
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`)
    },
    {
      id: 'email',
      label: 'Correo',
      icon: Mail,
      color: 'hover:bg-gray-500/20 text-gray-300',
      action: () => handleShare('email', (url) =>
        `mailto:?subject=${encodeURIComponent(propertyTitle)}&body=${encodeURIComponent(`${shareText}\n\n${url}`)}`)
    }
  ]

  const handleCopy = async () => {
    const result = await generateShortUrl()
    const url = result?.url || propertyUrl
    const code = result?.code || null
    try {
      await navigator.clipboard.writeText(url)
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = url
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
    await trackEvent('copy', code)
    setCopied(true)
    setTimeout(() => { setCopied(false); setIsOpen(false) }, 1500)
  }

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!isOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      setMenuPos({
        top: rect.bottom + window.scrollY + 8,
        left: rect.right + window.scrollX - 192
      })
    }
    setIsOpen(!isOpen)
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (btnRef.current && !btnRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const btnSize = size === 'sm' ? 'p-1.5' : 'p-2'
  const iconSize = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'

  const dropdown = isOpen ? createPortal(
    <div
      style={{ position: 'absolute', top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
      className="w-48 bg-slate-800 border border-white/10 rounded-xl shadow-2xl overflow-hidden"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
        <span className="text-white text-xs font-semibold uppercase tracking-wide">Compartir</span>
        <button onClick={() => setIsOpen(false)} className="p-0.5 hover:bg-white/10 rounded transition-colors">
          <X className="h-3 w-3 text-white/60" />
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center gap-2 px-3 py-2 text-white/50 text-xs">
          <Loader2 className="h-3 w-3 animate-spin" />
          Generando enlace...
        </div>
      )}

      <div className="py-1">
        {shareOptions.map((option) => {
          const Icon = option.icon
          return (
            <button
              key={option.id}
              onClick={option.action}
              disabled={loading}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors disabled:opacity-50 ${option.color}`}
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              {option.label}
            </button>
          )
        })}
      </div>

      <div className="border-t border-white/10 p-2">
        <button
          onClick={handleCopy}
          disabled={loading}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm hover:bg-white/10 transition-colors disabled:opacity-50"
        >
          {copied ? (
            <><Check className="h-4 w-4 text-green-400 flex-shrink-0" /><span className="text-green-400">¡Copiado!</span></>
          ) : (
            <><Copy className="h-4 w-4 text-white/70 flex-shrink-0" /><span className="text-white/70">Copiar enlace</span></>
          )}
        </button>
      </div>
    </div>,
    document.body
  ) : null

  return (
    <div className={`relative ${className}`}>
      <button
        ref={btnRef}
        onClick={handleOpen}
        className={`${btnSize} rounded-lg bg-emerald-500/80 hover:bg-emerald-500 transition-colors`}
        title="Compartir propiedad"
      >
        <Share2 className={`${iconSize} text-white`} />
      </button>
      {dropdown}
    </div>
  )
}

export default ShareButton
